// ============================================================================
// api_slaveMode.ts
// Mode Esclave (régie esclave → régie maître)
// ============================================================================

import { SlaveRefs, SlaveCallbacks } from '../../dataTypes/liveStreamManagment/types_signaling';
import { parseSdpCodec, getSupportedVideoCodecNames } from '../../dataTypes/liveStreamManagment/utils_sdp';
import { defaultRTCConfig } from '../../dataTypes/liveStreamManagment/utils_webrtc'; // ← opération 3

export const closeSlavePc = (refs: SlaveRefs): void => {
  if (refs.slavePcRef.current) { refs.slavePcRef.current.close(); refs.slavePcRef.current = null; }
  refs.slaveIceBufRef.current        = [];
  refs.slaveRemoteDescSetRef.current = false;
};

const makeSlavePc = (): RTCPeerConnection =>
  new RTCPeerConnection(defaultRTCConfig()); // ← opération 3

const getSlaveVideoTrack = async (
  refs: SlaveRefs,
  callbacks: SlaveCallbacks
): Promise<MediaStreamTrack | null> => {
  if (refs.canvasStreamRef.current?.getVideoTracks().length) {
    return refs.canvasStreamRef.current.getVideoTracks()[0];
  }
  if (!refs.currentStreamRef.current) return null;
  const srcEl = callbacks.getSourceVideoEl();
  if (!srcEl) return null;

  if (srcEl.srcObject !== refs.currentStreamRef.current) {
    srcEl.srcObject = refs.currentStreamRef.current;
    await srcEl.play().catch(() => {});
    if (!srcEl.videoWidth) {
      await new Promise<void>(resolve => {
        srcEl.onloadedmetadata = () => resolve();
        setTimeout(resolve, 2000);
      });
    }
  }

  const cs = callbacks.initCanvasStream(srcEl, 30);
  refs.canvasStreamRef.current = cs;
  const previewEl = callbacks.getCanvasPreviewEl();
  if (previewEl) { previewEl.srcObject = cs; previewEl.play().catch(() => {}); }
  return cs.getVideoTracks()[0] ?? null;
};

// ─── Helpers PeerConnection esclave ──────────────────────────────────────────

const attachSlaveTracks = (
  pc: RTCPeerConnection,
  videoTrack: MediaStreamTrack,
  audioMixerDestRef: SlaveRefs['audioMixerDestRef']
): void => {
  pc.addTrack(videoTrack, new MediaStream([videoTrack]));
  const mixerStream = audioMixerDestRef.current?.stream;
  const audioTrack  = mixerStream?.getAudioTracks()[0];
  if (audioTrack) pc.addTrack(audioTrack, mixerStream!);
};

const attachSlaveConnectionHandlers = (
  pc: RTCPeerConnection,
  refs: SlaveRefs,
  callbacks: SlaveCallbacks,
  onFailed: () => void
): void => {
  pc.onicecandidate = (e) => {
    if (e.candidate && refs.slaveWsRef.current?.readyState === WebSocket.OPEN)
      refs.slaveWsRef.current.send(JSON.stringify({ type: 'ice-candidate', target: 'control', payload: e.candidate }));
  };
  pc.onconnectionstatechange = () => {
    const s = pc.connectionState;
    callbacks.addSdpLog({ direction: 'info', type: 'event', label: `🔗 Slave WebRTC: ${s}` });
    callbacks.setSlaveState(
      s === 'connected'  ? 'connected'     :
      s === 'failed'     ? 'failed'        :
      s === 'connecting' ? 'connecting'    : 'disconnected'
    );
    if (s === 'failed') onFailed();
  };
};

const flushSlaveIceBuf = async (pc: RTCPeerConnection, refs: SlaveRefs): Promise<void> => {
  for (const c of refs.slaveIceBufRef.current) {
    try { await pc.addIceCandidate(new RTCIceCandidate(c)); } catch {}
  }
  refs.slaveIceBufRef.current = [];
};

// ─── Offer ────────────────────────────────────────────────────────────────────

export const slaveCreateOffer = async (
  refs: SlaveRefs,
  callbacks: SlaveCallbacks
): Promise<void> => {
  closeSlavePc(refs);
  if (refs.slaveWsRef.current?.readyState !== WebSocket.OPEN) return;

  const videoTrack = await getSlaveVideoTrack(refs, callbacks);
  if (!videoTrack) {
    callbacks.addSdpLog({ direction: 'info', type: 'event', label: '⚠ Slave: pas de video track canvas — attente du flux' });
    return;
  }

  const pc = makeSlavePc();
  refs.slavePcRef.current = pc;
  attachSlaveTracks(pc, videoTrack, refs.audioMixerDestRef);
  attachSlaveConnectionHandlers(pc, refs, callbacks, () => {
    closeSlavePc(refs);
    if (refs.slaveShouldReconnectRef.current)
      setTimeout(() => { if (refs.slaveShouldReconnectRef.current) slaveCreateOffer(refs, callbacks); }, 3000);
  });

  const offer = await pc.createOffer();
  callbacks.addSdpLog({ direction: 'sent', type: 'offer', label: 'Slave Offer → maître', sdp: offer.sdp, codec: offer.sdp ? parseSdpCodec(offer.sdp) : undefined });
  await pc.setLocalDescription(offer);

  refs.slaveWsRef.current!.send(JSON.stringify({
    type: 'offer', target: 'control', payload: offer,
    supported_codecs: getSupportedVideoCodecNames(),
  }));
};

// ─── Dispatch messages entrants ───────────────────────────────────────────────

export const handleSlaveSignalingMessage = async (
  msg: any,
  refs: SlaveRefs,
  callbacks: SlaveCallbacks
): Promise<void> => {
  switch (msg.type) {
    case 'auth_success':
      callbacks.addSdpLog({ direction: 'received', type: 'event', label: '✅ Slave auth succès' });
      callbacks.setSlaveState('connecting');
      await slaveCreateOffer(refs, callbacks);
      break;

    case 'auth_error':
      callbacks.addSdpLog({ direction: 'received', type: 'error', label: '❌ Slave auth error — vérifier identifiant/mot de passe' });
      callbacks.setSlaveState('failed');
      refs.slaveShouldReconnectRef.current = false;
      break;

    case 'offer': {
      callbacks.addSdpLog({ direction: 'received', type: 'offer', label: 'Slave: Offer ← maître', sdp: msg.payload?.sdp });
      closeSlavePc(refs);
      const videoTrack = await getSlaveVideoTrack(refs, callbacks);
      if (!videoTrack) break;

      const pc = makeSlavePc();
      refs.slavePcRef.current = pc;
      attachSlaveTracks(pc, videoTrack, refs.audioMixerDestRef);
      attachSlaveConnectionHandlers(pc, refs, callbacks, () => {
        callbacks.setSlaveState('failed');
      });

      await pc.setRemoteDescription(msg.payload);
      refs.slaveRemoteDescSetRef.current = true;
      await flushSlaveIceBuf(pc, refs);

      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      callbacks.addSdpLog({ direction: 'sent', type: 'answer', label: 'Slave Answer → maître', sdp: answer.sdp });
      refs.slaveWsRef.current?.send(JSON.stringify({
        type: 'answer', target: 'control', payload: answer, supported_codecs: [],
      }));
      break;
    }

    case 'answer':
      callbacks.addSdpLog({
        direction: 'received', type: 'answer', label: 'Slave Answer ← maître',
        sdp: msg.payload?.sdp, codec: msg.payload?.sdp ? parseSdpCodec(msg.payload.sdp) : undefined,
      });
      if (!refs.slavePcRef.current) break;
      await refs.slavePcRef.current.setRemoteDescription(msg.payload);
      refs.slaveRemoteDescSetRef.current = true;
      await flushSlaveIceBuf(refs.slavePcRef.current, refs);
      break;

    case 'ice-candidate': {
      const candidate = msg.payload;
      if (!refs.slavePcRef.current || !refs.slaveRemoteDescSetRef.current) {
        refs.slaveIceBufRef.current.push(candidate);
        break;
      }
      try { await refs.slavePcRef.current.addIceCandidate(new RTCIceCandidate(candidate)); } catch {}
      break;
    }

    case 'device_rejected':
      callbacks.addSdpLog({ direction: 'received', type: 'error', label: '❌ Slave rejeté par le maître', raw: msg });
      callbacks.setSlaveState('failed');
      break;
  }
};

// ─── Start / Stop ─────────────────────────────────────────────────────────────

export const startSlaveMode = async (
  refs: SlaveRefs,
  callbacks: SlaveCallbacks
): Promise<void> => {
  const cfg = refs.slaveSettingsRef.current;
  if (!cfg.masterWsUrl || !cfg.deviceId || !cfg.password) {
    callbacks.addSdpLog({ direction: 'info', type: 'event', label: '⚠ Slave: URL WS, identifiant et mot de passe requis' });
    return;
  }
  refs.slaveShouldReconnectRef.current = true;
  callbacks.setSlaveState('connecting');
  callbacks.addSdpLog({ direction: 'info', type: 'event', label: `🔗 Slave → maître: ${cfg.masterWsUrl}` });

  const ws = new WebSocket(cfg.masterWsUrl);
  refs.slaveWsRef.current = ws;

  ws.onopen    = () => ws.send(JSON.stringify({
    type: 'auth', device_id: cfg.deviceId, password: cfg.password,
    supported_codecs: getSupportedVideoCodecNames(),
  }));
  ws.onmessage = async (e) => {
    try { await handleSlaveSignalingMessage(JSON.parse(e.data), refs, callbacks); }
    catch (err) { console.error('Erreur parsing slave message:', err); }
  };
  ws.onerror = () => {
    callbacks.addSdpLog({ direction: 'received', type: 'error', label: '❌ Slave WS error' });
    callbacks.setSlaveState('failed');
  };
  ws.onclose = () => {
    callbacks.setSlaveState('disconnected');
    if (refs.slaveShouldReconnectRef.current) {
      callbacks.addSdpLog({ direction: 'info', type: 'event', label: '🔄 Slave: reconnexion dans 5s…' });
      refs.slaveReconnectTimeoutRef.current = setTimeout(() => {
        if (refs.slaveShouldReconnectRef.current) startSlaveMode(refs, callbacks);
      }, 5000);
    }
  };

  callbacks.setIsSlave(true);
};

export const stopSlaveMode = (refs: SlaveRefs, callbacks: SlaveCallbacks): void => {
  refs.slaveShouldReconnectRef.current = false;
  if (refs.slaveReconnectTimeoutRef.current) {
    clearTimeout(refs.slaveReconnectTimeoutRef.current);
    refs.slaveReconnectTimeoutRef.current = null;
  }
  closeSlavePc(refs);
  if (refs.slaveWsRef.current) {
    refs.slaveWsRef.current.onclose = null;
    refs.slaveWsRef.current.close();
    refs.slaveWsRef.current = null;
  }
  callbacks.setIsSlave(false);
  callbacks.setSlaveState('disconnected');
  callbacks.addSdpLog({ direction: 'info', type: 'event', label: '⏹ Slave déconnecté' });
};
